<template>
  <div class="photos-gallery">
    <GalleryTrigger
      v-if="experience?.id"
      ref="galleryTriggerRef"
      :gallery-origin="galleryOrigin"
      :gallery-photos-list="galleryPhotosList"
      :more-photos-count="photos.length"
      :preview-type="GalleryPreviewType.EXPERIENCE_SLIDER_GALLERY"
      :title="expTitle"
      :trigger-max-photos-count="MAX_PHOTOS_COUNT"
      :sogu-url="soguUrl"
      @on-trigger-click="decreaseZIndex"
      @on-close-gallery-slider="increaseZIndex"
      @on-close-gallery-list="increaseZIndex"
    />
  </div>
</template>

<script setup lang="ts">
import {
  GalleryOriginType,
  GalleryPreviewType,
  GalleryTrigger,
  type IGalleryTriggerProps,
} from '@experience/gallery-slider';
import { useI18n } from '@experience/i18n';
import { computed, onMounted, ref } from 'vue';

import { getRuntimeConfig } from '@/shared/config/runtime';
import { useHeaderZIndex } from '@/shared/lib/headerZIndex';

import ru from '../i18n/ru.json';

import type { ExperiencePhoto, PhotosPreviewExperience } from '../types/experience';

/**
 * Галерея вынесена из PhotosPreview в отдельный компонент, чтобы пакет gallery-slider (с модалкой и слайдером)
 * грузился отдельным чанком и не попадал в стартовый бандл: нужен он только после клика по фото.
 */
type Props = {
  photos: ExperiencePhoto[];
  experience?: PhotosPreviewExperience;
};

type GalleryTriggerExpose = InstanceType<typeof GalleryTrigger> & {
  onTriggerClick: (index: number) => Promise<void>;
};

type GallerySlide = IGalleryTriggerProps['galleryPhotosList'][number];

const MAX_PHOTOS_COUNT = 3;

const props = defineProps<Props>();

const emit = defineEmits<{
  ready: [];
}>();

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();
const soguUrl = getRuntimeConfig().VITE_RUNTIME_SOGU_SERVICE_URL;

const galleryTriggerRef = ref<GalleryTriggerExpose | null>(null);

const expTitle = computed(() => props.experience?.title || '');

const galleryOrigin = computed(() => ({
  id: props.experience?.id || 0,
  type: GalleryOriginType.EXPERIENCE,
  title: expTitle.value,
  description: '',
  number: 1,
}));

const galleryPhotosList = computed((): GallerySlide[] =>
  props.photos.map((photo, index) => ({
    id: `photos-preview-${props.experience?.id ?? 0}-${index}`,
    index,
    originId: props.experience?.id || 0,
    originType: GalleryOriginType.EXP_PLAN,
    srcPhoto: photo.medium || photo.thumbnail || '',
    thumbPhoto: photo.thumbnail || photo.medium || '',
    alt: t('photoAlt', { index: index + 1 }),
  })),
);

const open = async (index: number) => {
  decreaseZIndex();
  await galleryTriggerRef.value?.onTriggerClick(index);
};

defineExpose({ open });

onMounted(() => {
  emit('ready');
});
</script>

<style scoped lang="scss">
.photos-gallery :deep(.gallery-trigger) {
  display: none;
}
</style>
